import { Component, Input, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { CallGraph, CallMetrics } from '../../../analytics-core/interfaces/sem-calls.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';

@Component({
	selector: 'app-sem-calls-r10',
	styleUrls: ['./sem-calls-r10.component.css'],
	templateUrl: './sem-calls-r10.component.html'
})
export class SemCallsR10Component extends BaseWidgetComponent implements OnInit {
	@Input() uikitresponseconfig: any;
	@Input() customchartconfiguration: any;
	@Input() sectionclass: string;
	@Input() legend: string;
	@Input() chartconfiguration: any;
	@Input() colors: string;
	@Input() tertiarykey: string;
	@Input() sectiontitle: string;

	graphFilterOptions: any = [
		{ name: 'all calls', isAllCalls: true },
		{ name: '1-30 seconds', isAnsweredCalls: true, min: 1, max: 30 },
		{ name: '31-60 seconds', isAnsweredCalls: true, min: 31, max: 60 },
		{ name: '>60 seconds', isAnsweredCalls: true, min: 61 },
		{ name: 'missed', isMissedCalls: true }
	];

	selectedValue: number = 0;
	graphFilterDisabled: boolean = false;
	metricsData: any = false;
	chartMetricData: any = false;
	HOURS_PER_DAY = 24;
	SECONDS_PER_HOUR = 3600;
	FACTOR_FOR_MILLISECONDS = 1000;
	MILLISECONDS_PER_DAY = this.HOURS_PER_DAY * this.SECONDS_PER_HOUR * this.FACTOR_FOR_MILLISECONDS;
	public defaultCount: number = 14;
	public chartObj: any;
	public scatterplotConfiguration: any;
	public scatterPlotresponseConfiguration: object[];
	public allGraphData: CallGraph[] = [];
	public defaultChartConfiguration: any;
	public legends: any;
	public graphData: CallGraph[] = [];
	public graphTimeInterval: number = this.analyticsDataModel.config.version === 'R10' ? 4 : 6;
	public colorPaletteObj = {
		primaryColor: this.colorUtilityService.getHEXByColorType(
			this.analyticsDataModel.partner.colorPalette,
			'primary',
			1
		),
		secondaryColor: this.colorUtilityService.getHEXByColorType(
			this.analyticsDataModel.partner.colorPalette,
			'secondary',
			1
		),
		tertiaryColor: this.colorUtilityService.getHEXByColorType(
			this.analyticsDataModel.partner.colorPalette,
			'tertiary',
			1
		),
		// tslint:disable-next-line:object-literal-sort-keys
		quaternaryColor: this.colorUtilityService.getHEXByColorType(
			this.analyticsDataModel.partner.colorPalette,
			'quaternary',
			1
		),
		quinaryColor: this.colorUtilityService.getHEXByColorType(this.analyticsDataModel.partner.colorPalette, 'quinary', 1)
	};

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		super(analyticsService);
	}

	ngOnInit(): void {
		this.chartconfiguration = JSON.parse(decodeURI(this.customchartconfiguration));
		this.defaultChartConfiguration = JSON.parse(decodeURI(this.customchartconfiguration));
		this.scatterPlotresponseConfiguration = JSON.parse(decodeURI(this.uikitresponseconfig));
		this.legends = JSON.parse(decodeURI(this.legend));
		this.graphFilterDisabled = true;
		super.ngOnInit();
	}

	requestData(): void {
		this.hasData = false;
		this.showLoad = true;
		this.graphFilterDisabled = true;
		this.chartMetricData = false;
		this.metricsData = false;

		if (this.requestSubscription) {
			this.requestSubscription.unsubscribe();
		}
		this.requestSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
			if (response.success) {
				this.mapCallMetricsDataTointerface(response.data);
				if (this.metricsData) {
					this.hasData = false;
					this.selectedValue = 1;
					this.analyticsService.processWidgetRequest(this.tertiarykey).subscribe((response2: any) => {
						this.showLoad = false;
						if (response2.success) {
							this.hasData = true;
							this.graphFilterDisabled = false;
							this.graphData = this.mapCallGraphDataToInterface(response2.data);
							if (this.graphData && this.graphData.length > 0) {
								this.generateGraphData(this.graphData);
								this.drawGraph();
							}
						}
						this.showLoad = false;
					});
				} else {
					this.hasData = false;
					this.showLoad = false;
				}
			} else {
				this.metricsData = false;
				this.hasData = false;
				this.showLoad = false;
			}
		});
	}

	filterChange(selectedValue: any): void {
		this.showLoad = true;
		const filterOptions = this.graphFilterOptions.filter((optn, index) => index === Number(selectedValue));
		this.filterGraphData(filterOptions[0]);
		this.filterSeriesConfig(filterOptions[0]);
		this.filterChartSummaryData(filterOptions[0]);
	}

	filterGraphData(filterOption: any): void {
		if (!filterOption.isMissedCalls) {
			// filter by answered calls
			this.graphData = this.allGraphData.filter((data) => {
				if (filterOption.min && filterOption.max) {
					return Number(data.callDurations) <= filterOption.max && Number(data.callDurations) >= filterOption.min;
				} else if (filterOption.min && !filterOption.max) {
					return Number(data.callDurations) >= filterOption.min;
				} else if (!filterOption.min && filterOption.max) {
					return Number(data.callDurations) <= filterOption.max;
				} else {
					return true;
				}
			});
		} else {
			// filter by missed calls
			this.graphData = this.allGraphData.filter((data) => {
				return data.missedCalls && !data.answeredCalls;
			});
		}
	}

	filterSeriesConfig(filterOption: any): void {
		this.chartconfiguration.series = this.defaultChartConfiguration.series;
		if (filterOption.isMissedCalls) {
			this.chartconfiguration.series = [this.defaultChartConfiguration.series[1]];
		} else if (filterOption.isAnsweredCalls) {
			this.chartconfiguration.series = [this.defaultChartConfiguration.series[0]];
		}
		this.showLoad = false;
	}

	filterChartSummaryData(filterOption: any): void {
		this.chartMetricData = { ...this.metricsData };
		if (filterOption.isMissedCalls) {
			this.chartMetricData.missedCalls = this.graphData.length > 0 ? this.graphData.length : null;
			this.chartMetricData.avgDuration =
				this.analyticsDataModel?.config?.version != 'R10' ? null : this.chartMetricData.avgDuration;
			this.chartMetricData.answeredCalls = null;
		} else if (filterOption.isAnsweredCalls) {
			this.chartMetricData.missedCalls = null;
			this.chartMetricData.avgDuration = this.graphData.length ? this.getAvgDurationDateFormat() : null;
			this.chartMetricData.answeredCalls = this.graphData.length > 0 ? this.graphData.length : null;
		}
	}

	getAvgDurationDateFormat(): Date {
		let sum = 0;
		this.graphData.forEach((data) => {
			sum = sum + Number(data.callDurations);
		});
		sum = sum / this.graphData.length;
		const avgDuration = new Date();
		avgDuration.setHours(Math.floor(sum / this.SECONDS_PER_HOUR));
		avgDuration.setMinutes(Math.floor((sum / 60) % 60));
		avgDuration.setSeconds(Math.floor(sum % 60));
		return avgDuration;
	}

	getTickInterval(xMin: number, xMax: number): number {
		const days = (xMax - xMin) / this.MILLISECONDS_PER_DAY;
		return days < 21 ? this.MILLISECONDS_PER_DAY : Math.ceil(days / 20) * this.MILLISECONDS_PER_DAY;
	}

	getToolTipConfigHTML(): void {
		this.chartconfiguration.toolTip = {
			...this.chartconfiguration.toolTip,
			...{
				enabled: true,
				// tslint:disable-next-line:typedef
				pointFormatter() {
					const series = this.series;
					const x = this.x;
					const y = this.y;
					let count = 0;
					let counter = 0;
					let customProperty = '';
					let durationCount = '';

					const header = `<div class="legendClass">
														<table class="table" style="width: 160px;" >`;
					const footer = `	</table>
													</div>`;

					series.data.forEach((point) => {
						if (point.x === x && point.y === y) {
							count = count + 1;
						}
					});

					series.data.forEach((point) => {
						if (point.x === x && point.y === y) {
							counter = counter + 1;
							if (count > 1) {
								durationCount = `<span> ${counter} </span>`;
							}
							if (point.duration) {
								customProperty = `${customProperty}
									<tr style="display: flex; justify-content: space-between; text-transform: capitalize">
										<td>duration ${durationCount} </td>
										<td>
											${Highcharts.dateFormat('%H:%M:%S', point.duration * 1000)}
										</td>
									</tr>`;
							}
						}
					});
					const rows = `<tr style="display: flex; justify-content: space-between; text-transform: lowercase; text-transform: capitalize">
									<td style="color: ${this.series.color};">${count}  ${this.series.name} </td><td> ${Highcharts.dateFormat(
						'%b %e, %Y',
						this.x
					)} </td>
								</tr>
								<tr style="display: flex; justify-content: space-between; text-transform: capitalize">
									<td>time </td><td> ${Highcharts.dateFormat('%l:%M:%S %p', this.y)} </td>
								</tr>`;

					return `${header}
									${rows}
									${customProperty}
									${footer}`;
				},
				useHTML: true
			}
		};
	}

	updateXAxisConfig(stepCount: number): void {
		let xMin = moment.utc(this.analyticsDataModel.startDate.format('YYYYMMDD'), 'YYYYMMDD').unix() * 1000;
		let xMax = moment.utc(this.analyticsDataModel.endDate.format('YYYYMMDD'), 'YYYYMMDD').unix() * 1000;

		if (this.analyticsDataModel.startDate.format('YYYYMMDD') === this.analyticsDataModel.endDate.format('YYYYMMDD')) {
			xMax = xMin;
		} else {
			xMin = xMin - stepCount * this.MILLISECONDS_PER_DAY;
			xMax = xMax + stepCount * this.MILLISECONDS_PER_DAY;
		}

		this.chartconfiguration.xAxis[0].labels = {
			...this.chartconfiguration.xAxis[0].labels,
			...{
				// tslint:disable-next-line:typedef
				formatter() {
					return Highcharts.dateFormat(`%e <br> %b`, this.value);
				}
			}
		};
		this.chartconfiguration.xAxis[0] = {
			...this.chartconfiguration.xAxis[0],
			...{
				endOnTick: true,
				max: xMax,
				min: xMin,
				showLastLabel: true,
				startOnTick: true,
				tickInterval: this.getTickInterval(xMin, xMax)
			}
		};
	}

	updateYAxisConfig(): void {
		this.chartconfiguration.yAxis[0].labels = {
			...this.chartconfiguration.yAxis[0].labels,
			...{
				// tslint:disable-next-line:typedef
				formatter() {
					return Highcharts.dateFormat('%l:%M %p', this.value);
				}
			}
		};
		this.chartconfiguration.yAxis[0] = {
			...this.chartconfiguration.yAxis[0],
			...{
				max: this.MILLISECONDS_PER_DAY,
				min: 0,
				tickInterval: this.MILLISECONDS_PER_DAY / this.graphTimeInterval,
				type: 'datetime'
			}
		};
	}

	generateGraphData(graphData: any): void {
		graphData.forEach((obj) => {
			obj.dateObj = obj.dateObj * 1000;
			obj.missedCallsDateObj = UtilityService.isUndefinedOrNull(obj.missedCallsDateObj)
				? null
				: obj.missedCallsDateObj * 1000;
			obj.answeredCallsDateObj = UtilityService.isUndefinedOrNull(obj.answeredCallsDateObj)
				? null
				: obj.answeredCallsDateObj * 1000;
			let time = obj.time.split(':');
			time =
				(Number(time[0]) * this.SECONDS_PER_HOUR + Number(time[1]) * 60 + Number(time[2])) *
				this.FACTOR_FOR_MILLISECONDS;
			obj.answeredCallTime = obj.answeredCalls ? time : obj.answeredCallTime;
			obj.missedCallTime = obj.missedCallTime ? time : obj.missedCallTime;
		});

		if (typeof this.analyticsDataModel.startDate === 'string') {
			this.analyticsDataModel.startDate = moment(this.analyticsDataModel.startDate);
		}
		if (typeof this.analyticsDataModel.endDate === 'string') {
			this.analyticsDataModel.endDate = moment(this.analyticsDataModel.endDate);
		}
		const stepCount = this.calculateStepCountByDate();

		this.updateXAxisConfig(stepCount);
		this.updateYAxisConfig();
		this.getToolTipConfigHTML();
	}

	drawGraph(): void {
		if (this.graphData) {
			this.selectedValue = 0;
			this.allGraphData = this.graphData;
			this.graphFilterDisabled = false;
			this.scatterplotConfiguration = {
				chartConfig: this.chartconfiguration,
				responseConfig: this.scatterPlotresponseConfiguration
			};
		}
	}

	mapCallGraphDataToInterface(responseArray: any[]): CallGraph[] {
		const callsData: CallGraph[] = [];
		for (const response of responseArray) {
			const durations = response.calls_duration.split(',');
			durations.forEach((duration) => {
				const callGraphData: CallGraph = {} as CallGraph;
				callGraphData.dateObj = Number(response.date);
				callGraphData.time = response.time;
				callGraphData.answeredCallTime = Number(response.answered_calls_count) ? response.time : null;
				callGraphData.missedCallTime = Number(response.missed_calls_count) ? response.time : null;
				callGraphData.callDurations = duration;
				callGraphData.answeredCalls = Number(response.answered_calls_count) ? 1 : 0;
				callGraphData.missedCalls = Number(response.missed_calls_count) ? 1 : 0;
				callGraphData.missedCallsDateObj = Number(response.missed_calls_count) ? Number(response.date) : null;
				callGraphData.answeredCallsDateObj = Number(response.answered_calls_count) ? Number(response.date) : null;
				callsData.push(callGraphData);
			});
		}
		return callsData;
	}

	getAvgDuration(response: any): number {
		if (!UtilityService.isUndefinedOrNull(response[0].avg_duration_in_minutes)) {
			// const avgInSeconds = Number(response[0].avg_duration_in_minutes) * 60;
			// const avgDuration = new Date();
			// avgDuration.setSeconds(avgInSeconds % 60);
			// avgDuration.setMinutes(Math.floor(avgInSeconds / 60));
			// avgDuration.setHours(Math.floor(avgInSeconds / this.SECONDS_PER_HOUR));
			return response[0].avg_duration_in_minutes;
		}
		return null;
	}

	getTotalCalls(response: any): number {
		let totalCalls = !UtilityService.isUndefinedOrNull(response[0].answered_calls)
			? Math.round(Number(response[0].answered_calls))
			: 0;
		totalCalls = !UtilityService.isUndefinedOrNull(response[0].missed_calls)
			? totalCalls + Math.round(Number(response[0].missed_calls))
			: totalCalls;
		return totalCalls;
	}

	mapCallMetricsDataTointerface(response: any): void {
		if (response !== undefined) {
			if (response[0] && response.length) {
				const metricsData: CallMetrics = {} as CallMetrics;
				metricsData.answeredCalls = !UtilityService.isUndefinedOrNull(response[0].answered_calls)
					? Math.round(Number(response[0].answered_calls))
					: 0;
				metricsData.missedCalls = !UtilityService.isUndefinedOrNull(response[0].missed_calls)
					? Math.round(Number(response[0].missed_calls))
					: 0;
				metricsData.avgDuration = this.getAvgDuration(response);
				metricsData.totalCalls = this.getTotalCalls(response);
				if (!metricsData.answeredCalls && !metricsData.missedCalls && !metricsData.avgDuration) {
					this.metricsData = false;
				} else {
					this.metricsData = metricsData;
					this.chartMetricData = metricsData;
				}
			} else {
				this.metricsData = false;
			}
		}
	}

	protected calculateStepCountByDate(): number {
		const count = this.analyticsDataModel.endDate.diff(this.analyticsDataModel.startDate, 'days');
		if (count <= this.defaultCount) {
			return 1;
		}
		return Math.ceil(count / this.defaultCount);
	}
}
